<template>
    <div>
        <v-container class="ma-n3 pa-n3">
            <v-row no-gutters align="center" justify="space-between" class="mr-n6">
                <h2>
                    Open Notices
                </h2>
                <v-menu offset-y>
                    <template v-slot:activator="{ on }">
                        <v-btn small tile v-on="on"> <v-icon>save_alt</v-icon>&nbsp;Export </v-btn>
                    </template>
                    <v-list>
                        <v-list-item @click="exportCsv()">
                            <v-list-item-title> <v-icon>grid_on</v-icon>&nbsp;Excel (CSV) </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </v-row>
        </v-container>

        <v-card>
            <v-data-table
                :items="this.defects"
                :items-per-page="options.itemsPerPage"
                :sort-by="options.sortBy"
                :headers="headers"
                :loading="$wait.is(['loading defects'])"
                :footer-props.sync="footerProps"
                :server-items-length="totalItems"
                :options.sync="options"
            >
                <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
                <template v-slot:item="props">
                    <tr :class="classes(props.item)" @click="!disabled ? $router.push(props.item.isComplete ? {} : { query: { defectId: props.item.id, initiateFrom: 'schedule-defect' } }) : null">
                        <td>{{ props.item.ednNumber }}</td>
                        <td>{{ moment(props.item.receivedDate).format(dateFormat) }}</td>
                        <td>{{ props.item.description }} <v-icon v-if="props.item.photoBlobUri">image</v-icon></td>
                        <td>{{ moment(props.item.scheduledDate).format(dateFormat) }}</td>
                    </tr>
                </template>
                <template v-slot:no-results>
                    <v-layout row align-center>
                        <v-flex>No open notices found</v-flex>
                    </v-layout>
                </template>
            </v-data-table>
        </v-card>
    </div>
</template>

<script>
import { Event } from '@/plugins/bus';
import GarageLocationsClient from '@/components/GarageLocations/GarageLocationsClient.vue';
import { DateFormat } from '@/plugins/date-filter';
import moment from 'moment';
import DownloadHelper from '@/lib/DownloadHelper';

export default {
    mixins: [GarageLocationsClient],
    components: {},
    props: {
        unit: {
            required: true
        }
    },
    data() {
        return {
            dateFormat: DateFormat.DefaultDisplay,
            defects: [],
            options: {
                itemsPerPage: 10,
                page: 1,
                sortBy: ['receivedDate'],
                sortDesc: [true]
            },
            footerProps: {
                itemsPerPageOptions: [10, 50, 100]
            },
            headers: [
                { text: 'EDN', value: 'ednNumber', sortable: true },
                { text: 'Received', value: 'receivedDate', sortable: true },
                { text: 'Description', value: 'description', sortable: true },
                { text: 'Scheduled', value: 'scheduledDate', sortable: true }
            ],
            totalItems: 0
        };
    },
    computed: {
        unitId() {
            return this.$route.params.unitId;
        },
        params() {
            return {
                pageSize: this.options && this.options.itemsPerPage,
                page: this.options && this.options.page,
                unitId: this.unitId,
                orderBy: this.options.sortBy && this.options.sortBy.length >= 1 ? this.options.sortBy[0] : 'receivedDate',
                orderByDescending: this.options.sortDesc && this.options.sortDesc.length >= 1 ? this.options.sortDesc[0] : false,
                openOnly: true,
                includeDeleted: true
            };
        },
        disabled() {
            const g = this.unit.garageLocationId;
            return this.unit.isDeleted || (!this.userIsWorkshopManagerInGarage(g, this.userGarages) && !this.userIsDataEntryInGarage(g, this.userGarages) && !this.userIsFleetManager);
        },
        querystring() {
            if (!this.params) {
                return '';
            }
            return (
                '?' +
                Object.keys(this.params)
                    .filter(key => !!this.params[key])
                    .map(key => key + '=' + this.params[key])
                    .join('&')
            );
        }
    },
    methods: {
        async exportCsv() {
            const results = await this.getDefectsForReport();
            const data = results ? results : [];

            const rows = data.map(x => [
                x.unitNumber,
                x.ednNumber,
                x.description,
                x.reason,
                x.rating ? x.rating.name : '',
                x.source ? x.source.name : '',
                x.category ? x.category.name : '',
                x.createdDate ? moment(x.createdDate).format(DateFormat.DefaultDisplay) : '',
                x.receivedDate ? moment(x.receivedDate).format(DateFormat.DefaultDisplay) : '',
                x.scheduledDate ? moment(x.scheduledDate).format(DateFormat.DefaultDisplay) : '',
                x.scheduledReasonCategory,
                x.scheduledReasonNotes,
                x.reasonForExtension,
                x.extensionDate ? moment(x.extensionDate).format(DateFormat.DefaultDisplay) : ''
            ]);

            const csv = DownloadHelper.makeCsv(
                [
                    'Unit',
                    'EDN',
                    'Repair Description',
                    'Reason',
                    'Rating',
                    'Source',
                    'Category',
                    'Created Date',
                    'Received Date',
                    'Expected Date',
                    'Scheduled Reason Category',
                    'Scheduled Reason Notes',
                    'Reason For Extension',
                    'Extended Until Date'
                ],
                rows
            );
            DownloadHelper.download(`${this.unit.number}_OpenNotices_${moment().format(DateFormat.Canonical)}.csv`, new Blob([csv]));
        },
        async handleDefectCompleted() {
            await this.getDefects();
        },
        async getDefects() {
            try {
                this.$wait.start('loading defects');
                this.$wait.end('loading data');
                const response = await this.$api.get(`defects${this.querystring}`);
                this.defects = response.data.data;
                this.totalItems = response.data.total;
            } catch (error) {
                this.$toast.error('There was an error getting defects');
                this.defects = [];
            } finally {
                this.$wait.end('loading defects');
                this.$wait.end('loading data');
            }
        },
        async getDefectsForReport() {
            try {
                this.$wait.start('loading defects');
                this.$wait.end('loading data');
                const response = await this.$api.get(`defects/scheduled-defects-report?unitId=${this.unitId}`);
                return response.data;
            } catch (error) {
                this.$toast.error('There was an error generating the Open Notices report');
                return null;
            } finally {
                this.$wait.end('loading defects');
                this.$wait.end('loading data');
            }
        },
        classes(defect) {
            return {
                sweptForward: !!defect.sweptForwardByScheduler,
                'past-due': !defect.isComplete && defect.isPastDue
            };
        }
    },
    async mounted() {
        await this.getDefects();
        this.$bus.$on(Event.DefectCompleted, this.handleDefectCompleted);
        this.$bus.$on(Event.DefectPhotoDeleted, this.handleDefectCompleted);
    },
    destroyed() {
        this.$bus.$off(Event.DefectCompleted, this.handleDefectCompleted);
        this.$bus.$off(Event.DefectPhotoDeleted, this.handleDefectCompleted);
    },
    watch: {
        async unitId(val) {
            this.scheduledServices = await this.getDefects();
        },
        options: {
            async handler() {
                await this.getDefects();
            },
            deep: true
        }
    }
};
</script>
<style>
td {
    cursor: pointer;
}
</style>
